// src/components/Dashboard/PublicDashboard.tsx
import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  FileText,
  ShieldAlert,
  Sparkles,
  BookOpen,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const PublicDashboard: React.FC = () => {
  const { user } = useAuth();

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-[#097173] to-[#26ABB0] rounded-xl p-6 text-white shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-white/20 text-white mb-2">
              <Sparkles className="h-3.5 w-3.5" />
              Informational Access
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">
              Welcome, {user?.name || 'User'}
            </h1>
            <p className="text-[#E0F7F6] text-sm mt-1 max-w-xl">
              Explore DiagnomIQ precision intelligence for non-clinical, sample and informational MDT reports.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/legal?tab=medical-disclaimer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm font-medium transition-colors"
            >
              <ShieldAlert className="h-4 w-4" />
              Medical Disclaimer
            </Link>
          </div>
        </div>
      </div>

      {/* Informational Notice Card */}
      <div className="card border-l-4 border-l-[#097173] bg-teal-50/50 p-4 rounded-lg flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-[#097173] shrink-0 mt-0.5" />
        <div className="text-sm text-neutral-700 leading-relaxed">
          <strong className="text-neutral-900 font-semibold">Important Notice:</strong> DiagnomIQ reports generated through public accounts are for <strong>educational and informational purposes only</strong>. They do not constitute formal medical diagnoses, clinical treatment plans, or professional medical advice. Always consult a certified healthcare professional for medical decisions.
        </div>
      </div>

      {/* Quick Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card p-5 space-y-3">
          <div className="p-3 w-fit rounded-lg bg-teal-100/60 text-[#097173]">
            <FileText className="h-6 w-6" />
          </div>
          <h3 className="font-semibold text-neutral-900 text-base">Sample MDT Reports</h3>
          <p className="text-sm text-neutral-600 leading-relaxed">
            View sample multi-disciplinary team reports to understand clinical reasoning and biomarker correlation models.
          </p>
          <div className="pt-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-400">
              <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
              Included in your subscription
            </span>
          </div>
        </div>

        <div className="card p-5 space-y-3">
          <div className="p-3 w-fit rounded-lg bg-emerald-100/60 text-emerald-700">
            <BookOpen className="h-6 w-6" />
          </div>
          <h3 className="font-semibold text-neutral-900 text-base">Knowledge Base</h3>
          <p className="text-sm text-neutral-600 leading-relaxed">
            Access documentation, guides, and oncology terminology resources explaining precision health insights.
          </p>
          <div className="pt-2">
            <Link
              to="/legal?tab=terms-and-conditions"
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#097173] hover:underline"
            >
              Learn more <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        <div className="card p-5 space-y-3">
          <div className="p-3 w-fit rounded-lg bg-neutral-100 text-neutral-700">
            <CheckCircle className="h-6 w-6 text-emerald-600" />
          </div>
          <h3 className="font-semibold text-neutral-900 text-base">Subscription Status</h3>
          <p className="text-sm text-neutral-600 leading-relaxed">
            Your public membership account is active. Manage your profile and subscription settings at any time.
          </p>
          <div className="pt-2">
            <Link
              to="/profile"
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#097173] hover:underline"
            >
              View Profile <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicDashboard;
